import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageAutoRestart({ navigate }) {
  const link = (id, text) => (
    <button onClick={() => navigate(id)} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">{text}</button>
  );
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Auto Restart</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Long unattended sessions end in the dullest ways: the game crashes, the server kicks you, or
        the game slowly gets tired after hours of map changes. Auto Restart handles all three. It
        lives in the <strong className="text-white">MortisEngine loader</strong>, on its main page:
        on a timer it closes your games, starts them again, logs in and picks the farm back up, and
        it does the same when a game crashes or gets kicked in between.
      </p>

      <Callout type="info">
        <strong>One switch does everything.</strong> With Auto restart on, you get both the timed
        restarts and the restarts after a crash or a kick. With it off, the loader restarts nothing.
        It is off until you switch it on; if it is off, the loader asks you once whether to turn it on.
      </Callout>

      <DocHeading>Before You Turn It On</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The bot cannot restart the game on its own &mdash; it closes together with the game. The
        loader does the closing and the relaunching, so it has to be there:
      </p>
      <DocTable
        headers={['You need', 'Why']}
        rows={[
          [<strong className="text-white">The loader open and logged in</strong>, 'It does the restarting, and it needs your login and an active subscription to inject again. While the loader is closed, nothing is restarted.'],
          [<strong className="text-white">The game started from the official launcher, then injected with the loader</strong>, 'The loader restarts each game exactly the way it was started, so it has to have injected it.'],
          [<strong className="text-white">Inject again after a manual login</strong>, 'If you log in through the launcher again, the previous session stops working. Inject once into the new game and the restart uses the fresh one.'],
        ]}
      />

      <DocHeading>Set It Up</DocHeading>
      <Step n={1} title="Open the loader, log in and inject your games">
        Every game you inject shows up under <strong>GAME INSTANCES</strong> with a green{' '}
        <strong>INJECTED</strong> chip and the character&rsquo;s name.
      </Step>
      <Step n={2} title="Turn on the Auto restart switch">
        It is the bar above the game list. From now on every injected game is watched.
      </Step>
      <Step n={3} title="Set how often">
        Type the minutes after <strong>every</strong>: anywhere from <strong>30</strong> to{' '}
        <strong>240</strong>, default <strong>120</strong>. The new number counts when you press
        Enter or click elsewhere, so typing &ldquo;120&rdquo; does not start a round at
        &ldquo;1&rdquo;. A number outside the range is set to the nearest limit.
      </Step>
      <Step n={4} title="Start farming in the game as usual">
        The countdown on the right of the bar starts as soon as at least one watched character is
        in the game world.
      </Step>

      <Shot
        name="auto-restart-01-loader.png"
        tab="MortisEngine loader › main page"
        caption="The loader with Auto restart on: every 120 minutes, the next round in 1:23:45, and two injected clients farming."
        shoot="loadershot (a loader UIManager.cpp-je, álnevekkel): a főoldal — adatkártya, az Auto restart sáv BE (every 120 min, next 1:23:45), két INJECTED sor „Tinkerbolt” és „Emberwisp”, mindkettő ● Farming."
      />

      <DocHeading>The Auto Restart Bar</DocHeading>
      <DocTable
        headers={['Part', 'What it does']}
        rows={[
          [<strong className="text-white">Switch</strong>, 'On: every injected game is watched and restarted. Off: nothing is restarted, and the bar turns red with OFF on the right while games are running, so an off switch cannot go unnoticed. Space and Enter do not flip it - only a click does.'],
          [<strong className="text-white">every N min</strong>, <>One number for every game on this computer. Lowering it while the clock runs: if the new time has already passed, the round starts <strong>1 minute</strong> later, so a typo can still be fixed.</>],
          [<strong className="text-white">next</strong>, <>The time left until the next round (<strong>1:23:45</strong>). <strong>--</strong> while no watched character is in the world yet (the clock is not running), and <strong>now</strong> while a round is under way.</>],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        The loader remembers the switch and the interval, so they are the same after you restart the
        loader.
      </p>

      <DocHeading>The Client Rows</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Each injected game has a row: the character&rsquo;s name and, on the right, what the loader is
        doing with it. The tick in the row says whether this game is watched &mdash; untick it and this
        one game is left alone, even in timed rounds. The tick is not remembered: after a loader
        restart every injected game is watched again. Hover a row for a longer explanation, with the
        reason when something went wrong.
      </p>
      <DocTable
        headers={['Status', 'What it means']}
        rows={[
          [<span className="text-emerald-400">Farming</span>, 'Watched. The character is in the world.'],
          [<span className="text-gray-400">Character select</span>, 'Watched. The character is at character select.'],
          [<span className="text-gray-400">Not watched</span>, 'Auto restart is off, or you unticked this game. It will not be restarted.'],
          [<span className="text-amber-300">Closing...</span>, 'The loader is closing this game.'],
          [<span className="text-amber-300">Restart in 0:18 (2/5)</span>, 'Waiting to restart. The number in brackets is how many restarts failed in a row; it stops after 5.'],
          [<span className="text-amber-300">Starting... / Injecting... / Logging in...</span>, 'The restart is in progress.'],
          [<span className="text-amber-300">Waiting for party 2/3</span>, 'In the world, waiting for the rest of the party so everyone starts together.'],
          [<span className="text-emerald-400">Started, checking 7:12</span>, 'Running again. After 10 minutes without problems the restart counts as successful.'],
          [<span className="text-orange-400">Offline, retry in 14:32</span>, 'The server or your internet is down. The loader tries again every 15 minutes, all night if it has to.'],
          [<span className="text-orange-400">Can&rsquo;t start, retry in 29:10</span>, 'The loader cannot start the game right now: no active subscription, not logged in, or the bot could not be downloaded. It tries again every 30 minutes.'],
          [<span className="text-gray-400">Closed by you</span>, 'You closed this game yourself, so it will not be restarted.'],
          [<span className="text-red-400">Closed</span>, 'The game closed while Auto restart was off. Turning Auto restart on restarts it.'],
          [<span className="text-gray-400">Bot unloaded</span>, 'The game runs, but you unloaded the bot from it. It is not restarted until you inject again.'],
          [<span className="text-red-400">Stopped</span>, 'The loader gave up on this game. Hover the row to see why; turn Auto restart off and on to try again.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Rows of closed games stay in the list so you can see what happened; <strong className="text-white">Refresh</strong>{' '}
        clears them. The line under the list sums up the rest: for example{' '}
        <em>2 clients stopped. Turn Auto restart off and on to retry.</em>,{' '}
        <em>Server or internet outage - retrying every 15 min.</em>, or{' '}
        <em>Auto restart is OFF - closed games won&rsquo;t be restarted.</em>
      </p>

      <DocHeading>What Happens at Restart Time</DocHeading>
      <Step n={1} title="All watched games close together">
        The clock is shared: when it runs out, every watched game on this computer restarts in the
        same round, whenever each of them was started. The bot saves your settings first. The game
        is closed immediately &mdash; it does not wait for the end of a lap, so an unfinished lap and
        any loot still on the ground are left behind.
      </Step>
      <Step n={2} title="They start again, one by one">
        About 12 seconds apart, without stealing the focus from what you are doing. The loader
        injects each one as soon as it is ready.
      </Step>
      <Step n={3} title="The bot logs in with the same character">
        The one that was playing in that game before.
      </Step>
      <Step n={4} title="Parties wait for each other">
        Clients in the same party (see {link('party', 'Party')}) start together, once all of them are
        in the world. A member that does not make it in within 3 minutes does not hold the others
        back &mdash; it gets the start signal when it arrives.
      </Step>
      <Step n={5} title="The farm starts again — if it was running">
        After a restart the character usually wakes up in the hub, so the route starts from its
        beginning. If the last thing it ran was the {link('key-farm', 'Key Farm')}, the key farm
        starts instead, with this character&rsquo;s own role and levels. A game that was not farming
        before the restart stays idle.
      </Step>
      <p className="text-sm text-gray-400 mb-4">
        The clock starts again when the round is finished (every game is back, or its 3 minutes are
        up), so the whole interval goes to farming, not to loading screens.
      </p>

      <DocHeading>Crashes, Kicks and Stuck Games</DocHeading>
      <DocTable
        headers={['What happens', 'What the loader does']}
        rows={[
          ['The game crashes, or the server kicks you out', 'Restarts that game (and its party). A restart in between does not reset the shared clock.'],
          ['The game freezes, sits on a loading screen or loses the connection for 3 minutes', 'Closes it and starts it again.'],
          ['After a restart the game does not reach the world, or the route does not start, within 3 minutes', 'Counts it as a failed restart and tries again.'],
          ['Several restarts fail in a row', <>It waits longer each time: at once, then 1, 5, 15 and 60 minutes. After <strong>5</strong> failures in a row the row says <strong>Stopped</strong>. 10 minutes of trouble-free farming resets the count.</>],
          ['Every game drops at the same time (internet, server maintenance, sleeping computer)', 'That is an outage, not a failure: it tries every 15 minutes, all night, without giving up.'],
          ['The server says something a restart cannot fix', <>It stops at once and says why: <em>the game has to be updated</em>, <em>the account is logged in somewhere else</em>, <em>kicked by an admin or a restriction</em>, <em>the session is no longer valid - log in from the launcher and inject again</em>, or the same cause twice in a row.</>],
          ['The game closed at character select before it ever logged in', <>Stopped: <em>I don&rsquo;t know which character to log in with</em>. Log in once by hand.</>],
        ]}
      />

      <DocHeading>Logging In</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The bot logs in by itself only in a game the loader has just restarted, and only once: after
        the character has reached the world, character select is yours again. If you go there
        yourself &mdash; or the server sends you there &mdash; the game waits there until the next
        timed round.
      </p>

      <DocHeading>In the Game</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        While a game is watched, a small <strong className="text-white">RESTART&nbsp;&nbsp;1:23:45</strong>{' '}
        badge sits in the top-right corner of the game, even with the menu closed. It turns orange in
        the last minute and reads <strong className="text-amber-300">RESTART NOW</strong> when the round
        starts. In the menu, the <strong className="text-white">FARM SESSION</strong> card on{' '}
        {link('farm-stats', 'FARM › Stats')} has an <strong className="text-white">Auto restart</strong>{' '}
        line (<em>watched - next in 1:23:45</em>, <em>off in the loader</em>, <em>not watched by the
        loader</em>), and the on-screen status on {link('settings', 'Settings')} can show it too.
      </p>

      <DocHeading>Turning It Off, Closing Games, Closing the Loader</DocHeading>
      <DocTable
        headers={['You do', 'What happens']}
        rows={[
          ['Turn the switch off', 'Nothing new starts. A game that is being injected right now finishes, then is left alone. Party members waiting for each other get their start signal at once. The badge in the game disappears.'],
          ['Turn it on again', 'Every Stopped game, and every game that closed while it was off, is restarted right away. Games you closed yourself or unticked are not.'],
          ['Close a game yourself', <>Its row says <strong>Closed by you</strong>, and it is not restarted. In a party, the others keep farming.</>],
          ['Unload the bot (the END key)', <>The row says <strong>Bot unloaded</strong> and the game is not restarted. The END key only works while the game window is in front.</>],
          ['Close the loader', <>It asks first: <em>Closing the loader unloads the bot from N games. Auto restart stops too.</em> &mdash; <strong>Stay</strong> or <strong>Exit anyway</strong>. During a round it says <em>Auto restart is running</em> instead: games that are not injected yet stay at character select.</>],
        ]}
      />

      <DocHeading>Why Doesn&rsquo;t It Work?</DocHeading>
      <DocTable
        headers={['What you see', 'What to do']}
        rows={[
          ['The bar is red and says OFF', 'Auto restart is off. Click the switch.'],
          [<>The row says <strong>Not watched</strong></>, 'Auto restart is off, or the tick in that row is off.'],
          [<><strong>next</strong> stays on <strong>--</strong></>, 'No watched character is in the world yet. The clock only runs while at least one is.'],
          [<><strong>Can&rsquo;t start, retry in ...</strong></>, 'Check that the loader is logged in and your subscription is active. It tries again by itself.'],
          [<><strong>Stopped</strong></>, 'Hover the row for the reason. Fix it, then turn Auto restart off and on.'],
          ['The game restarts but stops at the login screen', 'The saved session is no longer valid, usually because you logged in through the launcher again. Start the game from the launcher and inject once.'],
          ['Back in the game, but the route does not start', 'The route was not running before the restart (by design), or the party is still waiting for a member - see the row.'],
        ]}
      />
    </div>
  );
}

export default PageAutoRestart;
